import {
  Cell,
  Column,
  Row,
  Table as AriaTable,
  TableBody,
  TableHeader,
} from "react-aria-components";
import type { ReactNode } from "react";
import type { TableProps as AriaTableProperties } from "react-aria-components";
import { cn } from "../../utilities/tailwind";

export type TableHeaderProperties = {
  /**
   * Optional className for the header border
   */
  className?: string | undefined;
  /**
   * Content to display in the header cell label
   */
  label: ReactNode;
};
export type TableProperties = {
  /**
   * Optional className applied to the underlying table element
   */
  className?: string | undefined;
  /**
   * Array of column header contents
   */
  headers: TableHeaderProperties[];
  /**
   * Array of table rows, each row is an array of cell contents
   */
  rows: ReactNode[][];
  /**
   * Whether to show row dividers
   */
  showRowDividers?: boolean | undefined;
  /**
   * Whether to apply alternating row background colors (striped rows)
   */
  stripedRows?: boolean | undefined;
} & Omit<AriaTableProperties, "children" | "className" | "style" | "slot">;
/**
 * Table component built with react-aria-components
 * Renders headers and rows from provided arrays with horizontal scroll on mobile
 */
export const Table = ({
  className = "",
  headers,
  rows,
  stripedRows = false,
  showRowDividers = true,
  ...properties
}: TableProperties) => {
  const twClassName = cn("min-w-full", className);

  return (
    <div className="overflow-x-auto">
      <AriaTable className={twClassName} {...properties}>
        <TableHeader>
          {headers.map((headerContent, headerIndex) => (
            <Column
              className="px-0 text-start whitespace-nowrap"
              isRowHeader={headerIndex === 0}
              key={headerIndex}
            >
              {headerContent.label}
              <div className={cn(headerContent.className)}></div>
            </Column>
          ))}
        </TableHeader>
        <TableBody className="border-b border-outline">
          {rows.map((rowCells, rowIndex) => (
            <Row
              className={
                stripedRows && rowIndex % 2 === 1
                  ? "bg-surface-container-low"
                  : ""
              }
              key={rowIndex}
            >
              {rowCells.map((cellContent, cellIndex) => (
                <Cell
                  className={cn(
                    "py-2 whitespace-nowrap",
                    showRowDividers && "border-b border-outline",
                  )}
                  key={cellIndex}
                >
                  {cellContent}
                </Cell>
              ))}
            </Row>
          ))}
        </TableBody>
      </AriaTable>
    </div>
  );
};
Table.displayName = "Table";
